import { useRouteError, useNavigate, isRouteErrorResponse } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Home, RefreshCcw, AlertTriangle, Coffee } from "lucide-react"
import * as Sentry from "@sentry/react"
import { useEffect } from "react"

export default function ErrorPage() {
    const error = useRouteError()
    const navigate = useNavigate()

    const getErrorMessage = (err: unknown): string => {
        if (isRouteErrorResponse(err)) {
            return err.statusText
        } else if (err instanceof Error) {
            return err.message
        } else if (typeof err === 'string') {
            return err
        }
        return "Unknown error"
    }

    const errorMessage = getErrorMessage(error)

    useEffect(() => {
        if (error) {
            Sentry.captureException(error)
        }
    }, [error])

    const handleReload = () => {
        window.location.reload()
    }

    const handleGoHome = () => {
        navigate("/", { replace: true })
    }

    return (
        <div className="min-h-screen bg-[#FFF4EC] flex items-center justify-center p-6 font-sans">
            <div className="max-w-md w-full text-center space-y-8 animate-in fade-in zoom-in duration-500">
                {/* Visual Icon */}
                <div className="relative mx-auto w-32 h-32">
                    <div className="absolute inset-0 bg-pink-200 rounded-full animate-ping opacity-25"></div>
                    <div className="relative bg-white rounded-3xl p-8 shadow-xl shadow-pink-200/50 border border-pink-100 flex items-center justify-center rotate-3">
                        <AlertTriangle size={64} className="text-[#E86A92]" />
                    </div>
                </div>

                {/* Content */}
                <div className="space-y-4">
                    <h1 className="text-3xl font-black italic text-[#4A3A3A] tracking-tight">
                        Ops! Algo desandou <br /> na nossa <span className="text-[#E86A92]">cozinha</span>.
                    </h1>
                    <p className="text-slate-500 leading-relaxed font-medium">
                        Não se preocupe, nossa equipe já foi notificada e está cuidando disso enquanto você toma um café.
                    </p>
                </div>

                {/* Developer Info (Subtle) */}
                {import.meta.env.DEV && error !== null && error !== undefined && (
                    <div className="p-4 bg-white/50 backdrop-blur-sm rounded-2xl border border-pink-100 text-left overflow-auto max-h-40">
                        <p className="text-[10px] font-mono text-pink-600 uppercase tracking-widest font-bold mb-2">Detalhes técnicos:</p>
                        <code className="text-xs text-slate-600">{errorMessage}</code>
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                    <Button
                        onClick={handleReload}
                        className="h-14 flex-1 rounded-2xl bg-[#E86A92] hover:bg-[#d4587e] text-white font-bold shadow-lg shadow-pink-200/50 group transition-all"
                    >
                        <RefreshCcw size={18} className="mr-2 group-hover:rotate-180 transition-transform duration-500" />
                        Tentar Novamente
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleGoHome}
                        className="h-14 flex-1 rounded-2xl border-2 border-slate-200 hover:border-[#E86A92] hover:text-[#E86A92] bg-white font-bold transition-all"
                    >
                        <Home size={18} className="mr-2" />
                        Voltar ao Início
                    </Button>
                </div>

                <div className="flex items-center justify-center gap-2 text-slate-400 text-xs font-bold uppercase tracking-[0.2em] pt-8">
                    <Coffee size={14} />
                    Equipe Cozinha Certa
                </div>
            </div>
        </div>
    )
}
